import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';

// Layout
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Pages
import HomePage from './pages/shop/HomePage';
import CollectionPage from './pages/shop/CollectionPage';
import ProductPage from './pages/shop/ProductPage';
import CartPage from './pages/shop/CartPage';
import WishlistPage from './pages/shop/WishlistPage';
import CheckoutPage from './pages/shop/CheckoutPage';
import OrderSuccessPage from './pages/shop/OrderSuccessPage';
import ProfilePage from './pages/shop/ProfilePage';
import AIAdvisorPage from './pages/shop/AIAdvisorPage';

// Auth
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// Admin
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import ProductFormPage from './pages/admin/ProductFormPage';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

// Shop Layout
const ShopLayout = ({ children }) => (
  <>
    <Navbar />
    <main style={{ minHeight: '80vh' }}>{children}</main>
    <Footer />
  </>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <Elements stripe={stripePromise}>
              <Toaster
                position="top-right"
                toastOptions={{
                  style: {
                    background: '#1a1a1a',
                    color: '#f5f0e8',
                    border: '1px solid rgba(201,168,76,0.3)',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '13px',
                  },
                  success: { iconTheme: { primary: '#c9a84c', secondary: '#0a0a0a' } },
                  error: { iconTheme: { primary: '#ef4444', secondary: '#0a0a0a' } },
                }}
              />
              <Routes>
                {/* Public Shop Routes */}
                <Route path="/" element={<ShopLayout><HomePage /></ShopLayout>} />
                <Route path="/collection" element={<ShopLayout><CollectionPage /></ShopLayout>} />
                <Route path="/product/:id" element={<ShopLayout><ProductPage /></ShopLayout>} />
                <Route path="/ai-advisor" element={<ShopLayout><AIAdvisorPage /></ShopLayout>} />

                {/* Auth Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />

                {/* Protected User Routes */}
                <Route path="/cart" element={<ProtectedRoute><ShopLayout><CartPage /></ShopLayout></ProtectedRoute>} />
                <Route path="/wishlist" element={<ProtectedRoute><ShopLayout><WishlistPage /></ShopLayout></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><ShopLayout><CheckoutPage /></ShopLayout></ProtectedRoute>} />
                <Route path="/order-success/:orderId" element={<ProtectedRoute><ShopLayout><OrderSuccessPage /></ShopLayout></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ShopLayout><ProfilePage /></ShopLayout></ProtectedRoute>} />

                {/* Admin Routes */}
                <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="products/new" element={<ProductFormPage />} />
                  <Route path="products/edit/:id" element={<ProductFormPage />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="users" element={<AdminUsers />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Elements>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
